import { Source } from "../Source";

interface Props {
  id: string;
  audioBuffer: AudioBuffer;
  position: number;
  destination: AudioNode;
  audioContext: AudioContext;
}

/**
 * Source implementation for AudioBuffer playback.
 *
 * Receives a pre-decoded AudioBuffer and handles play/pause/seek.
 * Data loading and waveform computation are handled by RegionLoader.
 */
export class AudioBufferSource extends Source {
  private sourceNode: AudioBufferSourceNode | null = null;
  private destination: AudioNode;
  private _audioContext: AudioContext;
  private _audioBuffer: AudioBuffer;
  private timestamp: number;
  private _position: number;

  constructor({ id, audioBuffer, position, destination, audioContext }: Props) {
    super(id)

    this.destination = destination
    this._audioContext = audioContext;
    this._audioBuffer = audioBuffer;
    this._position = position;
    this.timestamp = 0;
  }

  /**
   * Get the duration of this source in seconds.
   */
  get duration(): number {
    return this._audioBuffer.duration;
  }

  /**
   * Get the position (start time) of this source in seconds.
   */
  get position(): number {
    return this._position;
  }

  /**
   * Get the underlying AudioBuffer.
   */
  get audioBuffer(): AudioBuffer {
    return this._audioBuffer;
  }

  /**
   * Get the AudioContext.
   */
  get audioContext(): AudioContext {
    return this._audioContext;
  }

  /**
   * Set the position (start time) of this source.
   */
  setPosition(position: number): void {
    this._position = position;
  }

  /**
   * Replace the internal audio buffer.
   */
  replaceBuffer(newBuffer: AudioBuffer): void {
    this._audioBuffer = newBuffer;
  }

  // Play from before region (startPosition < position)
  //        v
  // -------------[xxxxxx] -----
  // when = position - startPosition
  // offset = 0

  // Play after start of region (startPosition > position)
  //                  v
  // -------------[xxxxxx] -----
  // when = now
  // offset = startPosition - position

  // combines to
  // when = max(position - startPosition, 0)
  // offset = max(startPosition - position, 0)
  play(timestamp: number, playbackRate: number = 1.0) {
    this.timestamp = timestamp;
    const when = this._audioContext.currentTime + Math.max(this._position - this.timestamp, 0) / playbackRate;
    const offset = Math.max(this.timestamp - this._position, 0);

    // Don't play if we're past the end of this region
    if (offset >= this._audioBuffer.duration) {
      return;
    }

    this.sourceNode = this._audioContext.createBufferSource();
    this.sourceNode.playbackRate.value = playbackRate;
    this.sourceNode.connect(this.destination);
    this.sourceNode.buffer = this._audioBuffer;
    this.sourceNode.start(when, offset);
  }

  pause() {
    if (this.sourceNode) {
      this.sourceNode.stop();
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
  }

  async seek(timestamp: number) {
    this.pause();
    this.timestamp = timestamp;
  }
}
